import { useEffect, useState, useCallback } from "react";
import {
  Box, Button, IconButton, LinearProgress, Typography, CircularProgress,
} from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import QuizQuestion from "./QuizQuestion";
import { overlayVariants, panelVariants, questionVariants } from "./quizMotion";
import { APIGetQuizDetail, APISubmitQuiz } from "../../api_quiz";

/**
 * Полноэкранный анимированный «проходчик» теста.
 * props: open, code, token, onClose(), onCompleted(code)
 */
export default function QuizRunner({ open, code, token, onClose, onCompleted }) {
  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // { [qid]: {option_ids, numeric_value} }
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [saving, setSaving] = useState(false);
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !code) return;
    let alive = true;
    setLoading(true);
    setFinished(false);
    setIndex(0);
    setDirection(1);
    setError(null);
    (async () => {
      try {
        const data = await APIGetQuizDetail(token, code);
        if (!alive) return;
        setQuiz(data);
        setQuestions(data.questions || []);
        const map = {};
        (data.my_answers || []).forEach((a) => {
          map[a.question_id] = {
            option_ids: a.option_ids || [],
            numeric_value: a.numeric_value ?? null,
          };
        });
        setAnswers(map);
      } catch (e) {
        if (alive) setError("Не удалось загрузить тест");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, code, token]);

  const total = questions.length;
  const current = questions[index];
  const answer = current ? answers[current.id] : null;

  const isAnswered = (q, a) => {
    if (!q) return false;
    if (q.question_type === "scale") return a?.numeric_value != null;
    if (q.question_type === "single") return (a?.option_ids || []).length === 1;
    return (a?.option_ids || []).length >= 1; // multiple
  };

  const buildAnswersArray = useCallback((map) => (
    Object.entries(map)
      .filter(([, v]) => (v.option_ids && v.option_ids.length) || v.numeric_value != null)
      .map(([qid, v]) => ({
        question_id: Number(qid),
        option_ids: v.option_ids || [],
        numeric_value: v.numeric_value ?? null,
      }))
  ), []);

  const setAnswer = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  };

  const answeredCount = questions.filter((q) => isAnswered(q, answers[q.id])).length;
  const progressPct = total ? Math.round((answeredCount / total) * 100) : 0;
  const canProceed = !current?.is_required || isAnswered(current, answer);

  const autosave = useCallback(async (map) => {
    try {
      await APISubmitQuiz(token, code, { answers: buildAnswersArray(map), isFinal: false });
    } catch (e) {
      /* не мешаем прохождению */
    }
  }, [token, code, buildAnswersArray]);

  const goNext = async () => {
    if (index < total - 1) {
      setDirection(1);
      setIndex((i) => i + 1);
      autosave(answers);
    } else {
      setSaving(true);
      setError(null);
      try {
        const res = await APISubmitQuiz(token, code, {
          answers: buildAnswersArray(answers),
          isFinal: true,
        });
        if (res?.is_completed) {
          setFinished(true);
          onCompleted?.(code);
        } else {
          setError("Ответьте на все обязательные вопросы");
        }
      } catch (e) {
        setError("Не удалось сохранить. Попробуйте ещё раз");
      } finally {
        setSaving(false);
      }
    }
  };

  const goBack = () => {
    if (index > 0) {
      setDirection(-1);
      setIndex((i) => i - 1);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <Box
          component={motion.div}
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 12000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(20,10,40,0.45)",
            p: { xs: 0, sm: 2 },
          }}
        >
          <Box
            component={motion.div}
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            sx={{
              position: "relative",
              width: { xs: "100%", sm: 520 },
              height: { xs: "100%", sm: "auto" },
              maxHeight: { sm: "92vh" },
              backgroundColor: "background.surface",
              borderRadius: { xs: 0, sm: "xl" },
              boxShadow: "lg",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* header */}
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Typography level="title-md" noWrap sx={{ flex: 1 }}>
                  {quiz?.title || "Тест"}
                </Typography>
                <IconButton size="sm" variant="plain" color="neutral" onClick={onClose}>
                  <CloseRoundedIcon />
                </IconButton>
              </Box>
              {!loading && !finished && total > 0 && (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress determinate value={progressPct} thickness={6} sx={{ borderRadius: 10 }} />
                  <Typography level="body-xs" sx={{ mt: 0.5, color: "text.tertiary" }}>
                    Вопрос {Math.min(index + 1, total)} из {total}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* body */}
            <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2.5, minHeight: { xs: "auto", sm: 320 } }}>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
                  <CircularProgress />
                </Box>
              ) : finished ? (
                <CompletionView title={quiz?.title} onClose={onClose} />
              ) : total === 0 ? (
                <Typography sx={{ textAlign: "center", py: 6, color: "text.tertiary" }}>
                  В этом тесте пока нет вопросов
                </Typography>
              ) : (
                <AnimatePresence mode="wait" custom={direction}>
                  <Box
                    key={current?.id}
                    component={motion.div}
                    custom={direction}
                    variants={questionVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                  >
                    <QuizQuestion
                      question={current}
                      answer={answer}
                      onChange={(val) => setAnswer(current.id, val)}
                    />
                  </Box>
                </AnimatePresence>
              )}
            </Box>

            {/* footer */}
            {!loading && !finished && total > 0 && (
              <Box sx={{ px: 2.5, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
                {error && (
                  <Typography level="body-xs" color="danger" sx={{ mb: 1, textAlign: "center" }}>
                    {error}
                  </Typography>
                )}
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Button
                    variant="soft"
                    color="neutral"
                    disabled={index === 0 || saving}
                    startDecorator={<ArrowBackRoundedIcon />}
                    onClick={goBack}
                  >
                    Назад
                  </Button>
                  <Button
                    sx={{ flex: 1 }}
                    disabled={!canProceed || saving}
                    loading={saving}
                    endDecorator={index < total - 1 ? <ArrowForwardRoundedIcon /> : <CheckCircleRoundedIcon />}
                    onClick={goNext}
                  >
                    {index < total - 1 ? "Далее" : "Завершить"}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </AnimatePresence>
  );
}

function CompletionView({ title, onClose }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", py: 5 }}>
      <Box
        component={motion.div}
        initial={{ scale: 0, rotate: -30 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 18 }}
        sx={{
          width: 96,
          height: 96,
          borderRadius: "50%",
          background: "linear-gradient(135deg, var(--joy-palette-primary-400), var(--joy-palette-primary-600))",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2.5,
          boxShadow: "0 12px 30px rgba(124,58,237,0.4)",
        }}
      >
        <motion.svg width="52" height="52" viewBox="0 0 24 24" fill="none">
          <motion.path
            d="M4 12.5l5 5 11-11"
            stroke="#fff"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 0.2, duration: 0.5, ease: "easeInOut" }}
          />
        </motion.svg>
      </Box>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
        <Typography level="h3">Тест пройден!</Typography>
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}>
        <Typography level="body-sm" sx={{ color: "text.tertiary", mt: 0.5, maxWidth: 320 }}>
          «{title}» — ваши ответы сохранены. Теперь подбор будет точнее.
        </Typography>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}>
        <Button sx={{ mt: 3 }} onClick={onClose}>Готово</Button>
      </motion.div>
    </Box>
  );
}